import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Chip, CircularProgress, Pagination } from "@mui/material";
import { statusColor, statusLabel } from "./supportTicketStatus";

const when = (value) => (value ? new Date(value).toLocaleString() : "");

const TicketRow = ({ ticket, to, showPartner }) => (
  <li>
    <Link
      to={to}
      className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 no-underline hover:border-teal-300"
    >
      <div className="min-w-0">
        <p className="m-0 flex items-center gap-2 text-sm font-semibold text-slate-900">
          {ticket.unread ? (
            <span
              className="inline-block h-2 w-2 shrink-0 rounded-full bg-rose-500"
              aria-label="New activity"
            />
          ) : null}
          <span className="truncate">
            #{ticket.id} · {ticket.title}
          </span>
        </p>
        <p className="m-0 mt-0.5 text-xs text-slate-500 truncate">
          {showPartner && ticket.partnerName ? `${ticket.partnerName} · ` : ""}
          Last activity {when(ticket.lastActivityAt)}
        </p>
      </div>
      <Chip size="small" color={statusColor(ticket.status)} label={statusLabel(ticket.status)} />
    </Link>
  </li>
);

/**
 * One page of tickets at a time, newest activity first. Used twice on each side: once for the open
 * tickets that need attention and once for the full history, each paging on its own.
 */
const SupportTicketList = ({
  queryKey,
  fetchPage,
  params,
  pageSize = 10,
  linkTo,
  showPartner = false,
  emptyText = "No tickets yet.",
}) => {
  const [page, setPage] = useState(0);
  const request = { ...params, page, size: pageSize };
  const { data, isLoading, isError } = useQuery([...queryKey, request], () => fetchPage(request), {
    keepPreviousData: true,
    refetchOnMount: true,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <CircularProgress size={22} />
      </div>
    );
  }
  if (isError) {
    return <p className="m-0 text-sm text-rose-600">Could not load tickets. Try again shortly.</p>;
  }

  const tickets = data?.content || [];
  if (!tickets.length) return <p className="m-0 text-sm text-slate-500">{emptyText}</p>;

  return (
    <div>
      <ul className="m-0 list-none space-y-2 p-0">
        {tickets.map((ticket) => (
          <TicketRow
            key={ticket.id}
            ticket={ticket}
            to={linkTo(ticket)}
            showPartner={showPartner}
          />
        ))}
      </ul>
      {data.totalPages > 1 ? (
        <div className="mt-3 flex justify-center">
          <Pagination
            size="small"
            count={data.totalPages}
            page={page + 1}
            onChange={(_, next) => setPage(next - 1)}
          />
        </div>
      ) : null}
    </div>
  );
};

export default SupportTicketList;
